import React, { useMemo, useState } from "react";
import { Button, Checkbox, Field, Input, Select, Tab, TabList, Textarea } from "@fluentui/react-components";
import { Add20Regular, ArrowDown16Regular, ArrowUp16Regular, Delete20Regular, Edit20Regular } from "@fluentui/react-icons";
import {
  GanttChart20Regular,
  Map20Regular,
  PeopleTeam20Regular,
  Rocket20Regular,
  Search20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import c from "./Taskplanning.module.css";
import { R, api, crud, iso, parseISO } from "./Api/Taskapi";
import type { Person } from "./Api/Taskapi";
import { FormDialog, Head, Page, Tile, Wait, asList, fmtDate, fmtStamp, mondayOf, nice, useAct, useLoad } from "./Taskpages";
import type { PageCtx } from "./Taskpages";

/* ------------------------------------------------------------------ *
 *  Planning & capacity: timeline / Gantt / critical path, workload and
 *  capacity, roadmap / epics / portfolios, baselines / risks, search /
 *  saved views / calendar / templates, and sprint planning.
 *  All data comes from the Task Manager API (views, agile, capacity,
 *  tasks and workspaces services).
 * ------------------------------------------------------------------ */

type Q = Record<string, string | number | boolean | null | undefined>;

/* ---------- small helpers ---------- */
const dayNum = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return Math.floor(Date.UTC(y || 1970, (m || 1) - 1, d || 1) / 86400000);
};
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (n: number) => {
  const d = new Date(n * 86400000);
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MON[d.getUTCMonth()]}`;
};
const hours = (h: number | null | undefined) => (h === null || h === undefined ? "—" : `${Math.round(h * 10) / 10}h`);
const mins = (m: number) => `${Math.round((m / 60) * 10) / 10}h`;
const personName = (people: Person[], id: number | null | undefined) => (id === null || id === undefined ? "—" : (people.find((p) => p.id === id)?.name ?? `User ${id}`));
const isAdmin = (ctx: PageCtx) => ctx.me.workspaces.find((w) => w.id === ctx.workspaceId)?.role === "ADMIN";
const healthTone = (h?: string | null) => (h === "RED" ? c.chipRed : h === "AMBER" ? c.chipAmber : h === "GREEN" ? c.chipGreen : c.chipGrey);
const Tag: React.FC<{ text: string; tone?: string | undefined }> = ({ text, tone }) => <span className={`${c.chip} ${tone ?? c.chipGrey}`}>{nice(text)}</span>;
const Meter: React.FC<{ pct: number; tone?: "good" | "warn" | "bad" | undefined }> = ({ pct, tone }) => (
  <div className={c.meter} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
    <div className={`${c.meterFill} ${tone === "good" ? c.meterGood : tone === "warn" ? c.meterWarn : tone === "bad" ? c.meterBad : ""}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
  </div>
);

/** project picker used by most pages */
function useProjectPick(ctx: PageCtx, allowAll = false) {
  const [pid, setPid] = useState<number | null>(ctx.projectId ?? (allowAll ? null : (ctx.projects[0]?.id ?? null)));
  const el = (
    <Select aria-label="Project" value={pid === null ? "" : String(pid)} onChange={(_, d) => setPid(d.value ? Number(d.value) : null)}>
      {allowAll ? <option value="">All projects</option> : ctx.projects.length === 0 ? <option value="">No projects</option> : null}
      {ctx.projects.map((p) => (
        <option key={p.id} value={String(p.id)}>
          {p.name}
        </option>
      ))}
    </Select>
  );
  return { pid, el };
}

/** icon + title + tabs frame shared by the pages in this file */
const Frame: React.FC<{
  icon: React.ReactNode;
  title: string;
  sub: string;
  tabs: [string, string][];
  tab: string;
  setTab: (t: string) => void;
  tools?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, sub, tabs, tab, setTab, tools, actions, children }) => (
  <>
    <Head icon={icon} title={title} sub={sub}>
      {actions}
    </Head>
    <div className={styles.bar}>
      <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
        {tabs.map(([k, label]) => (
          <Tab key={k} value={k}>
            {label}
          </Tab>
        ))}
      </TabList>
      {tools ? <div className={styles.tools}>{tools}</div> : null}
    </div>
    <Page>{children}</Page>
  </>
);

/* =================================================================== *
 *  Generic list + create/edit/delete for one CRUD entity
 * =================================================================== */
interface FieldDef {
  key: string;
  label: string;
  type: "text" | "textarea" | "number" | "date" | "select" | "bool";
  options?: { value: string; label: string }[];
  numeric?: boolean;
  required?: boolean;
  hint?: string;
  initial?: string;
}
interface RecordTableProps<T extends { id: number }> {
  ctx: PageCtx;
  title: string;
  noun: string;
  route: string;
  query?: Q;
  base: Record<string, unknown>;
  fields: FieldDef[];
  cols: { label: string; render: (r: T) => React.ReactNode }[];
  rowActions?: (r: T) => React.ReactNode;
  onChanged?: () => void;
  readOnly?: boolean;
  intro?: string;
  version?: number;
}

function RecordTable<T extends { id: number }>({ ctx, title, noun, route, query, base, fields, cols, rowActions, onChanged, readOnly, intro, version }: RecordTableProps<T>) {
  const act = useAct(ctx.notify);
  const q: Q = query ?? {};
  const l = useLoad(async () => (await crud.list<T>(route, q)).items ?? [], [route, JSON.stringify(q), version ?? 0]);
  const [dlg, setDlg] = useState<{ row: T | null } | null>(null);
  const [vals, setVals] = useState<Record<string, string | boolean>>({});
  const [busy, setBusy] = useState(false);
  const rows = l.data ?? [];

  const open = (row: T | null) => {
    const v: Record<string, string | boolean> = {};
    for (const f of fields) {
      const raw = row ? (row as unknown as Record<string, unknown>)[f.key] : undefined;
      if (f.type === "bool") v[f.key] = raw === true;
      else if (raw === null || raw === undefined) v[f.key] = f.initial ?? "";
      else v[f.key] = f.type === "date" ? String(raw).slice(0, 10) : String(raw);
    }
    setVals(v);
    setDlg({ row });
  };
  const missing = fields.some((f) => f.required && f.type !== "bool" && !String(vals[f.key] ?? "").trim());
  const submit = async () => {
    if (!dlg) return;
    const body: Record<string, unknown> = {};
    for (const f of fields) {
      const v = vals[f.key];
      if (f.type === "bool") body[f.key] = v === true;
      else {
        const s = String(v ?? "").trim();
        if (s === "") {
          if (dlg.row && !f.required) body[f.key] = null;
        } else body[f.key] = f.type === "number" || f.numeric ? Number(s) : s;
      }
    }
    setBusy(true);
    const ok = await act(() => (dlg.row ? crud.update(route, dlg.row.id, body) : crud.create(route, { ...base, ...body })), dlg.row ? "Saved." : `${nice(noun)} added.`);
    setBusy(false);
    if (ok) {
      setDlg(null);
      l.reload();
      onChanged?.();
    }
  };

  return (
    <div className={c.card}>
      <div className={c.cardHead}>
        <h3 className={c.cardTitle}>{title}</h3>
        {!readOnly && (
          <Button size="small" icon={<Add20Regular />} onClick={() => open(null)}>
            New {noun}
          </Button>
        )}
      </div>
      {intro && <p className={c.note}>{intro}</p>}
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && rows.length === 0 && `No ${noun}s yet`} onRetry={l.reload} />
      {rows.length > 0 && (
        <div className={c.scroll}>
          <table className={c.tbl}>
            <thead>
              <tr>
                {cols.map((col) => (
                  <th key={col.label}>{col.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  {cols.map((col) => (
                    <td key={col.label}>{col.render(r)}</td>
                  ))}
                  <td>
                    <div className={c.rowActions}>
                      {rowActions?.(r)}
                      {!readOnly && (
                        <>
                          <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label={`Edit ${noun}`} onClick={() => open(r)} />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label={`Delete ${noun}`}
                            onClick={async () => {
                              if (await act(() => crud.remove(route, r.id), `${nice(noun)} deleted.`)) {
                                l.reload();
                                onChanged?.();
                              }
                            }}
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FormDialog open={dlg !== null} title={dlg?.row ? `Edit ${noun}` : `New ${noun}`} action={dlg?.row ? "Save" : "Add"} busy={busy} disabled={missing} onClose={() => setDlg(null)} onSubmit={() => void submit()}>
        {fields.map((f) => {
          const v = vals[f.key];
          const set = (x: string | boolean) => setVals((s) => ({ ...s, [f.key]: x }));
          if (f.type === "bool") return <Checkbox key={f.key} label={f.label} checked={v === true} onChange={(_, d) => set(d.checked === true)} />;
          return (
            <Field key={f.key} label={f.label} required={!!f.required} hint={f.hint ?? null}>
              {f.type === "textarea" ? (
                <Textarea value={String(v ?? "")} onChange={(_, d) => set(d.value)} rows={4} resize="vertical" />
              ) : f.type === "select" ? (
                <Select value={String(v ?? "")} onChange={(_, d) => set(d.value)}>
                  {!f.required && <option value="">—</option>}
                  {f.required && !String(v ?? "") && <option value="">Choose…</option>}
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={String(v ?? "")} onChange={(_, d) => set(d.value)} />
              )}
            </Field>
          );
        })}
      </FormDialog>
    </div>
  );
}

const peopleOptions = (people: Person[]) => people.map((p) => ({ value: String(p.id), label: p.name }));

/* =================================================================== *
 *  Charts (inline SVG)
 * =================================================================== */
interface GRow {
  id: number;
  title: string;
  start: string;
  end: string;
  done: boolean;
  critical?: boolean;
  indent?: number;
  baseline?: { start?: string | null; end?: string | null } | null;
  color?: string | undefined;
}
interface GLink {
  from: number;
  to: number;
}
interface GMile {
  id: number;
  name: string;
  date: string;
  reached: boolean;
}
const LABEL_W = 220;
const ROW_H = 28;
const HEAD_H = 34;

export const GanttChart: React.FC<{ rows: GRow[]; links?: GLink[]; milestones?: GMile[]; onPick?: (id: number) => void; aria: string }> = ({ rows, links = [], milestones = [], onPick, aria }) => {
  const prepared = rows.map((r) => {
    let s = dayNum(r.start);
    let e = dayNum(r.end);
    if (e < s) [s, e] = [e, s];
    return { ...r, s, e };
  });
  const nums: number[] = [];
  for (const r of prepared) {
    nums.push(r.s, r.e);
    if (r.baseline?.start) nums.push(dayNum(r.baseline.start));
    if (r.baseline?.end) nums.push(dayNum(r.baseline.end));
  }
  for (const m of milestones) nums.push(dayNum(m.date));
  if (nums.length === 0) return <div className={styles.emptyRow}>Nothing with dates to draw yet</div>;
  const min = Math.min(...nums) - 2;
  const max = Math.max(...nums) + 3;
  const span = max - min;
  const dayW = Math.max(14, Math.min(44, 960 / span));
  const width = LABEL_W + span * dayW;
  const total = prepared.length + milestones.length;
  const height = HEAD_H + total * ROW_H + 8;
  const x = (d: number) => LABEL_W + (d - min) * dayW;
  const index = new Map(prepared.map((r, i) => [r.id, i]));
  const today = Math.floor(Date.now() / 86400000);
  const ticks: number[] = [];
  for (let d = min; d <= max; d++) if ((d + 3) % 7 === 0) ticks.push(d);
  const cut = (t: string, n: number) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

  return (
    <div className={c.ganttWrap}>
      <svg className={c.gantt} width={width} height={height} role="img" aria-label={aria}>
        <defs>
          <marker id="arr" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L8,4 L0,8 z" fill="#8a8a8a" />
          </marker>
        </defs>
        {ticks.map((d) => (
          <g key={d}>
            <line x1={x(d)} y1={HEAD_H - 6} x2={x(d)} y2={height} stroke="#eeeeee" />
            <text className={c.ganttAxis} x={x(d) + 3} y={HEAD_H - 12}>
              {dayLabel(d)}
            </text>
          </g>
        ))}
        {today >= min && today <= max && <line x1={x(today) + dayW / 2} y1={HEAD_H - 6} x2={x(today) + dayW / 2} y2={height} stroke="#c4314b" strokeDasharray="4 3" />}
        {prepared.map((r, i) => {
          const y = HEAD_H + i * ROW_H;
          const w = Math.max(dayW * (r.e - r.s + 1), 6);
          const fill = r.color ?? (r.done ? "#2ea043" : r.critical ? "#c4314b" : "#0f6cbd");
          return (
            <g key={r.id}>
              {i % 2 === 1 && <rect x={0} y={y} width={width} height={ROW_H} fill="#fafafa" />}
              <text className={`${c.ganttLabel} ${onPick ? c.ganttLabelClick : ""}`} x={8 + (r.indent ?? 0) * 12} y={y + 18} onClick={() => onPick?.(r.id)}>
                {cut(r.title, 30 - (r.indent ?? 0) * 2)}
                <title>{r.title}</title>
              </text>
              <rect x={x(r.s)} y={y + 5} width={w} height={r.baseline ? 12 : 16} rx={4} fill={fill} opacity={r.done ? 0.75 : 1}>
                <title>
                  {r.title}: {dayLabel(r.s)} – {dayLabel(r.e)}
                  {r.critical ? " (critical)" : ""}
                </title>
              </rect>
              {r.baseline?.start && r.baseline.end && (
                <rect x={x(dayNum(r.baseline.start))} y={y + 19} width={Math.max(dayW * (dayNum(r.baseline.end) - dayNum(r.baseline.start) + 1), 4)} height={4} rx={2} fill="#9a9a9a">
                  <title>
                    Baseline: {fmtDate(r.baseline.start)} – {fmtDate(r.baseline.end)}
                  </title>
                </rect>
              )}
            </g>
          );
        })}
        {links.map((l, i) => {
          const a = index.get(l.from);
          const b = index.get(l.to);
          if (a === undefined || b === undefined) return null;
          const pa = prepared[a];
          const pb = prepared[b];
          if (!pa || !pb) return null;
          const xa = x(pa.e + 1);
          const xb = x(pb.s);
          const ya = HEAD_H + a * ROW_H + 13;
          const yb = HEAD_H + b * ROW_H + 13;
          const mid = Math.max(xa + 6, Math.min(xb - 6, xa + 10));
          return <path key={i} d={`M${xa},${ya} H${mid} V${yb} H${xb}`} fill="none" stroke="#8a8a8a" strokeWidth="1.2" markerEnd="url(#arr)" />;
        })}
        {milestones.map((m, i) => {
          const y = HEAD_H + (prepared.length + i) * ROW_H;
          const cx = x(dayNum(m.date)) + dayW / 2;
          return (
            <g key={`m${m.id}`}>
              <text className={c.ganttLabel} x={8} y={y + 18}>
                ◆ {cut(m.name, 28)}
              </text>
              <rect x={cx - 7} y={y + 7} width={14} height={14} transform={`rotate(45 ${cx} ${y + 14})`} fill={m.reached ? "#2ea043" : "#f2994a"}>
                <title>
                  {m.name}: {fmtDate(m.date)}
                </title>
              </rect>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

interface Series {
  label: string;
  color: string;
  dashed?: boolean;
  values: (number | null)[];
}
const LineChart: React.FC<{ labels: string[]; series: Series[]; aria: string }> = ({ labels, series, aria }) => {
  if (labels.length < 2) return <div className={styles.emptyRow}>The chart appears once there are a few days of data</div>;
  const W = 640;
  const H = 220;
  const P = 34;
  const max = Math.max(1, ...series.flatMap((s) => s.values.map((v) => v ?? 0)));
  const x = (i: number) => P + (i / (labels.length - 1)) * (W - P * 2);
  const y = (v: number) => H - P - (v / max) * (H - P * 2);
  const path = (s: Series) => {
    let d = "";
    s.values.forEach((v, i) => {
      if (v === null) return;
      d += `${d === "" || s.values[i - 1] === null ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
    });
    return d;
  };
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className={c.chart} role="img" aria-label={aria}>
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#d1d1d1" />
        <line x1={P} y1={P / 2} x2={P} y2={H - P} stroke="#d1d1d1" />
        {series.map((s) => (
          <path key={s.label} d={path(s)} fill="none" stroke={s.color} strokeWidth={2.4} strokeDasharray={s.dashed ? "5 4" : undefined} />
        ))}
        <text x={P - 4} y={P / 2 + 8} fontSize="11" fill="#616161" textAnchor="end">
          {Math.round(max)}
        </text>
        <text x={P} y={H - 10} fontSize="11" fill="#616161">
          {labels[0]}
        </text>
        <text x={W - P} y={H - 10} fontSize="11" fill="#616161" textAnchor="end">
          {labels[labels.length - 1]}
        </text>
      </svg>
      <div className={c.legend}>
        {series.map((s) => (
          <span key={s.label}>
            <span className={c.legendDot} style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/* =================================================================== *
 *  Timeline / Gantt / critical path
 * =================================================================== */
interface TlTask {
  id: number;
  title: string;
  parentTaskId?: number | null;
  startDate?: string | null;
  dueDate?: string | null;
  isCompleted: boolean;
}
interface TlData {
  tasks: TlTask[] | null;
  dependencies: { predecessorTaskId: number; successorTaskId: number; dependencyType: string; lagDays: number }[] | null;
  milestones: { id: number; name: string; dueDate: string; isReached: boolean }[] | null;
}
interface GanttData {
  durationDays: number;
  criticalTaskIds: number[];
  tasks: { id: number; title: string; parentTaskId?: number | null; isCompleted: boolean; start: string; end: string; scheduled: boolean; critical: boolean; slack: number; baseline: { start?: string | null; end?: string | null } | null }[];
  links: { from: number; to: number; type: string; lag: number }[];
  milestones: { id: number; name: string; dueDate: string; isReached: boolean }[];
}
interface CpData {
  projectStart: string;
  durationDays: number;
  criticalTaskIds: number[];
  items: { id: number; title: string; isCompleted: boolean; duration: number; earliestStart: number; earliestFinish: number; latestStart: number; latestFinish: number; slack: number; critical: boolean; plannedStart: string; plannedFinish: string }[];
}
interface BaselineLite {
  id: number;
  name: string;
  capturedAt: string;
}
interface DepRow {
  id: number;
  predecessorTaskId: number;
  successorTaskId: number;
  dependencyType: string;
  lagDays: number;
}

export const TimelinePage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const { pid, el } = useProjectPick(ctx);
  const [tab, setTab] = useState("timeline");
  const [baselineId, setBaselineId] = useState("");
  const [depOpen, setDepOpen] = useState(false);
  const [dep, setDep] = useState({ pred: "", succ: "", type: "FS", lag: "0" });
  const [ver, setVer] = useState(0);

  const tl = useLoad(() => (pid !== null && tab === "timeline" ? api<TlData>("GET", "/api/views/timeline", undefined, { projectId: pid }) : Promise.resolve(null)), [pid, tab, ver]);
  const baselines = useLoad(async () => (pid !== null && tab === "gantt" ? asList<BaselineLite>(await api("GET", `/api/agile/projects/${pid}/baselines`)) : []), [pid, tab]);
  const gantt = useLoad(
    () => (pid !== null && (tab === "gantt" || tab === "links") ? api<GanttData>("GET", `/api/agile/projects/${pid}/gantt`, undefined, { baselineId: baselineId || undefined }) : Promise.resolve(null)),
    [pid, tab, baselineId, ver],
  );
  const cp = useLoad(() => (pid !== null && tab === "critical" ? api<CpData>("GET", `/api/tasks/projects/${pid}/critical-path`) : Promise.resolve(null)), [pid, tab, ver]);
  const taskIds = (gantt.data?.tasks ?? []).map((t) => t.id);
  const deps = useLoad(
    async () => (tab === "links" && taskIds.length > 0 ? (await crud.list<DepRow>(R_DEP, { "predecessorTaskId.in": taskIds.join(","), pageSize: 500 })).items : []),
    [tab, taskIds.join(","), ver],
  );

  const tlRows: GRow[] = useMemo(
    () =>
      (tl.data?.tasks ?? [])
        .filter((t) => t.startDate || t.dueDate)
        .map((t) => ({ id: t.id, title: t.title, start: (t.startDate ?? t.dueDate) as string, end: (t.dueDate ?? t.startDate) as string, done: t.isCompleted, indent: t.parentTaskId ? 1 : 0 })),
    [tl.data],
  );
  const ganttRows: GRow[] = (gantt.data?.tasks ?? []).map((t) => ({ id: t.id, title: t.title, start: t.start, end: t.end, done: t.isCompleted, critical: t.critical, indent: t.parentTaskId ? 1 : 0, baseline: t.baseline }));
  const titleOf = (id: number) => gantt.data?.tasks.find((t) => t.id === id)?.title ?? `Task ${id}`;
  const taskOptions = (gantt.data?.tasks ?? []).map((t) => ({ value: String(t.id), label: t.title }));

  return (
    <Frame
      icon={<GanttChart20Regular />}
      title="Timeline"
      sub="See the schedule, what depends on what, and the path that decides the finish date."
      tabs={[
        ["timeline", "Timeline"],
        ["gantt", "Gantt"],
        ["critical", "Critical path"],
        ["links", "Dependencies & milestones"],
      ]}
      tab={tab}
      setTab={setTab}
      tools={el}
    >
      {pid === null && <div className={styles.emptyRow}>Create a project first</div>}

      {tab === "timeline" && pid !== null && (
        <div className={c.card}>
          <Wait loading={tl.loading && !tl.data} error={tl.error} onRetry={tl.reload} />
          {tl.data && (
            <>
              <GanttChart
                aria="Project timeline"
                rows={tlRows}
                links={(tl.data.dependencies ?? []).map((d) => ({ from: d.predecessorTaskId, to: d.successorTaskId }))}
                milestones={(tl.data.milestones ?? []).map((m) => ({ id: m.id, name: m.name, date: m.dueDate, reached: m.isReached }))}
                onPick={ctx.openTask}
              />
              <p className={c.note}>
                Only tasks with a start or due date are drawn ({tlRows.length} of {(tl.data.tasks ?? []).length}). Click a task name to open it.
              </p>
            </>
          )}
        </div>
      )}

      {tab === "gantt" && pid !== null && (
        <div className={c.card}>
          <div className={c.cardHead}>
            <h3 className={c.cardTitle}>Gantt{gantt.data ? ` · ${gantt.data.durationDays} days` : ""}</h3>
            <Select aria-label="Compare with baseline" value={baselineId} onChange={(_, d) => setBaselineId(d.value)}>
              <option value="">No baseline</option>
              {(baselines.data ?? []).map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name} · {fmtDate(b.capturedAt)}
                </option>
              ))}
            </Select>
          </div>
          <Wait loading={gantt.loading && !gantt.data} error={gantt.error} onRetry={gantt.reload} />
          {gantt.data && (
            <>
              <GanttChart aria="Gantt chart" rows={ganttRows} links={gantt.data.links} milestones={gantt.data.milestones.map((m) => ({ id: m.id, name: m.name, date: m.dueDate, reached: m.isReached }))} onPick={ctx.openTask} />
              <div className={c.legend}>
                <span>
                  <span className={c.legendDot} style={{ background: "#0f6cbd" }} />
                  Task
                </span>
                <span>
                  <span className={c.legendDot} style={{ background: "#c4314b" }} />
                  Critical path
                </span>
                <span>
                  <span className={c.legendDot} style={{ background: "#2ea043" }} />
                  Done
                </span>
                <span>
                  <span className={c.legendDot} style={{ background: "#9a9a9a" }} />
                  Baseline
                </span>
              </div>
              {gantt.data.tasks.some((t) => !t.scheduled) && <p className={c.note}>Tasks without both a start and due date are placed where their dependencies and estimate say they would fit.</p>}
            </>
          )}
        </div>
      )}

      {tab === "critical" && pid !== null && (
        <div className={c.card}>
          <h3 className={c.cardTitle}>Critical path</h3>
          <Wait loading={cp.loading && !cp.data} error={cp.error} onRetry={cp.reload} />
          {cp.data && (
            <>
              <div className={c.tiles}>
                <Tile n={`${cp.data.durationDays} days`} label="Shortest possible finish" />
                <Tile n={cp.data.criticalTaskIds.length} label="Critical tasks" />
                <Tile n={fmtDate(cp.data.projectStart)} label="Project start" />
              </div>
              {cp.data.items.length === 0 ? (
                <div className={styles.emptyRow}>No tasks in this project</div>
              ) : (
                <div className={c.scroll}>
                  <table className={c.tbl}>
                    <thead>
                      <tr>
                        <th>Task</th>
                        <th className={c.num}>Days</th>
                        <th>Planned start</th>
                        <th>Planned finish</th>
                        <th className={c.num}>Latest finish (day)</th>
                        <th className={c.num}>Slack</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {cp.data.items.map((it) => (
                        <tr key={it.id} className={c.clickRow} onClick={() => ctx.openTask(it.id)}>
                          <td className={c.strong}>{it.title}</td>
                          <td className={c.num}>{it.duration}</td>
                          <td>{fmtDate(it.plannedStart)}</td>
                          <td>{fmtDate(it.plannedFinish)}</td>
                          <td className={c.num}>{it.latestFinish}</td>
                          <td className={c.num}>{it.slack}</td>
                          <td>{it.critical ? <Tag text="Critical" tone={c.chipRed} /> : it.isCompleted ? <Tag text="Done" tone={c.chipGreen} /> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "links" && pid !== null && (
        <>
          <div className={c.card}>
            <div className={c.cardHead}>
              <h3 className={c.cardTitle}>Dependencies</h3>
              <Button size="small" icon={<Add20Regular />} disabled={taskOptions.length < 2} onClick={() => setDepOpen(true)}>
                New dependency
              </Button>
            </div>
            <p className={c.note}>FS = finish to start, SS = start to start, FF = finish to finish, SF = start to finish.</p>
            <Wait loading={(deps.loading && !deps.data) || (gantt.loading && !gantt.data)} error={deps.error || gantt.error} empty={!deps.loading && (deps.data ?? []).length === 0 && "No dependencies yet"} onRetry={deps.reload} />
            {(deps.data ?? []).length > 0 && (
              <table className={c.tbl}>
                <thead>
                  <tr>
                    <th>Before</th>
                    <th>Type</th>
                    <th>After</th>
                    <th className={c.num}>Lag (days)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(deps.data ?? []).map((d) => (
                    <tr key={d.id}>
                      <td>{titleOf(d.predecessorTaskId)}</td>
                      <td>{d.dependencyType}</td>
                      <td>{titleOf(d.successorTaskId)}</td>
                      <td className={c.num}>{d.lagDays}</td>
                      <td>
                        <div className={c.rowActions}>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label="Delete dependency"
                            onClick={async () => {
                              if (await act(() => crud.remove(R_DEP, d.id), "Dependency removed.")) setVer((v) => v + 1);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <RecordTable<MilestoneRow>
            ctx={ctx}
            title="Milestones"
            noun="milestone"
            route="tasks/tsk/milestone"
            query={{ projectId: pid, pageSize: 200, sort: "dueDate" }}
            base={{ workspaceId: ctx.workspaceId, projectId: pid }}
            version={ver}
            onChanged={() => setVer((v) => v + 1)}
            fields={[
              { key: "name", label: "Name", type: "text", required: true },
              { key: "dueDate", label: "Due date", type: "date" },
              { key: "isReached", label: "Reached", type: "bool" },
            ]}
            cols={[
              { label: "Milestone", render: (r) => <span className={c.strong}>{r.name}</span> },
              { label: "Due", render: (r) => fmtDate(r.dueDate) },
              { label: "Status", render: (r) => <Tag text={r.isReached ? "Reached" : "Open"} tone={r.isReached ? c.chipGreen : c.chipAmber} /> },
            ]}
          />
          <FormDialog
            open={depOpen}
            title="New dependency"
            action="Add"
            disabled={!dep.pred || !dep.succ || dep.pred === dep.succ}
            onClose={() => setDepOpen(false)}
            onSubmit={async () => {
              const ok = await act(
                () => crud.create(R_DEP, { workspaceId: ctx.workspaceId, predecessorTaskId: Number(dep.pred), successorTaskId: Number(dep.succ), dependencyType: dep.type, lagDays: Number(dep.lag) || 0 }),
                "Dependency added.",
              );
              if (ok) {
                setDepOpen(false);
                setDep({ pred: "", succ: "", type: "FS", lag: "0" });
                setVer((v) => v + 1);
              }
            }}
          >
            <Field label="This task…" required>
              <Select value={dep.pred} onChange={(_, d) => setDep({ ...dep, pred: d.value })}>
                <option value="">Choose a task</option>
                {taskOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="…must come before" required>
              <Select value={dep.succ} onChange={(_, d) => setDep({ ...dep, succ: d.value })}>
                <option value="">Choose a task</option>
                {taskOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <div className={styles.formRow}>
              <Field label="Type" className={styles.grow}>
                <Select value={dep.type} onChange={(_, d) => setDep({ ...dep, type: d.value })}>
                  {["FS", "SS", "FF", "SF"].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Lag (days)" className={styles.grow}>
                <Input type="number" value={dep.lag} onChange={(_, d) => setDep({ ...dep, lag: d.value })} />
              </Field>
            </div>
          </FormDialog>
        </>
      )}
    </Frame>
  );
};
const R_DEP = "tasks/tsk/taskdependency";
interface MilestoneRow {
  id: number;
  name: string;
  dueDate?: string | null;
  isReached: boolean;
}

/* =================================================================== *
 *  Workload & capacity
 * =================================================================== */
interface OpenWork {
  from: string;
  weeks: number;
  items: { userId: number | null; displayName: string | null; week: string; tasks: number; estimateMinutes: number; overdue: number }[];
}
interface CapWeek {
  weekStart: string;
  capacityHours: number;
  leaveDays: number;
  allocatedHours: number;
  tentativeHours: number;
  plannedTaskHours: number;
  loggedHours: number;
  billableHours: number;
  availableHours: number;
  utilizationPercent: number | null;
  overAllocated: boolean;
}
interface CapData {
  from: string;
  to: string;
  people: { userId: number; displayName: string | null; weeks: CapWeek[] }[];
}
interface ForecastData {
  weeks: { weekStart: string; capacityHours: number; bookedHours: number; tentativeHours: number; plannedTaskHours: number; freeHours: number; utilizationPercent: number | null; overbookedPeople: number }[];
}
interface UtilData {
  people: { userId: number; displayName: string | null; role: string | null; capacityHours: number; loggedHours: number; billableHours: number; utilizationPercent: number | null; billablePercent: number | null; targetPercent: number; status: string }[];
}
interface CompareData {
  changedWeeks: number;
  rows: { userId: number; displayName: string | null; weekStart: string; liveHours: number; scenarioHours: number; difference: number; capacityHours: number }[];
}
interface ScenarioRow {
  id: number;
  name: string;
  description?: string | null;
  isBaseline: boolean;
}

const AdminOnly: React.FC = () => <div className={styles.emptyRow}>Only workspace admins can see this.</div>;
const heat = (v: number, max: number) => (v <= 0 ? "" : v / max > 0.8 ? c.heat5 : v / max > 0.6 ? c.heat4 : v / max > 0.4 ? c.heat3 : v / max > 0.2 ? c.heat2 : c.heat1);

export const WorkloadPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const admin = isAdmin(ctx);
  const [tab, setTab] = useState("open");
  const [weeks, setWeeks] = useState("8");
  const [from, setFrom] = useState(() => iso(mondayOf(new Date())));
  const [scenario, setScenario] = useState("");
  const [project, setProject] = useState("");
  const [uFrom, setUFrom] = useState(() => iso(new Date(Date.now() - 29 * 86400000)));
  const [uTo, setUTo] = useState(() => iso(new Date()));
  const [cmp, setCmp] = useState("");
  const [ver, setVer] = useState(0);

  const scenarios = useLoad(async () => (await crud.list<ScenarioRow>("time/cap/scenario", { workspaceId: ctx.workspaceId })).items ?? [], [ctx.workspaceId, ver]);
  const open = useLoad(
    () => (tab === "open" ? api<OpenWork>("GET", "/api/views/workload", undefined, { workspaceId: ctx.workspaceId, from, weeks: Number(weeks), projectId: project || undefined }) : Promise.resolve(null)),
    [tab, ctx.workspaceId, from, weeks, project],
  );
  const cap = useLoad(
    () => (tab === "capacity" ? api<CapData>("GET", "/api/capacity/workload", undefined, { workspaceId: ctx.workspaceId, from, weeks: Number(weeks), scenarioId: scenario || undefined }) : Promise.resolve(null)),
    [tab, ctx.workspaceId, from, weeks, scenario, ver],
  );
  const fc = useLoad(
    () => (tab === "forecast" && admin ? api<ForecastData>("GET", "/api/capacity/forecast", undefined, { workspaceId: ctx.workspaceId, from, weeks: Number(weeks), scenarioId: scenario || undefined }) : Promise.resolve(null)),
    [tab, admin, ctx.workspaceId, from, weeks, scenario, ver],
  );
  const ut = useLoad(
    () => (tab === "utilization" && admin ? api<UtilData>("GET", "/api/capacity/utilization", undefined, { workspaceId: ctx.workspaceId, from: uFrom, to: uTo }) : Promise.resolve(null)),
    [tab, admin, ctx.workspaceId, uFrom, uTo, ver],
  );
  const cmpData = useLoad(
    () => (tab === "scenarios" && admin && cmp ? api<CompareData>("GET", `/api/capacity/scenarios/${cmp}/compare`, undefined, { from, weeks: Number(weeks) }) : Promise.resolve(null)),
    [tab, admin, cmp, from, weeks, ver],
  );

  const weekStarts = useMemo(() => Array.from({ length: Number(weeks) }, (_, i) => iso(new Date(parseISO(from).getTime() + i * 7 * 86400000))), [from, weeks]);
  const openPeople = useMemo(() => {
    const m = new Map<string, { name: string; cells: Map<string, OpenWork["items"][number]> }>();
    for (const it of open.data?.items ?? []) {
      const key = String(it.userId ?? "none");
      const row = m.get(key) ?? { name: it.displayName ?? "Unassigned", cells: new Map() };
      row.cells.set(it.week.slice(0, 10), it);
      m.set(key, row);
    }
    return [...m.values()];
  }, [open.data]);
  const maxOpen = Math.max(1, ...(open.data?.items ?? []).map((i) => i.estimateMinutes / 60 || i.tasks));

  const range = (
    <>
      <Input type="date" aria-label="Start week" value={from} onChange={(_, d) => d.value && setFrom(iso(mondayOf(parseISO(d.value))))} />
      <Select aria-label="Weeks" value={weeks} onChange={(_, d) => setWeeks(d.value)}>
        {[4, 8, 12, 26].map((n) => (
          <option key={n} value={String(n)}>
            {n} weeks
          </option>
        ))}
      </Select>
      {(tab === "capacity" || tab === "forecast") && (
        <Select aria-label="Plan" value={scenario} onChange={(_, d) => setScenario(d.value)}>
          <option value="">Live plan</option>
          {(scenarios.data ?? []).map((s) => (
            <option key={s.id} value={String(s.id)}>
              What-if: {s.name}
            </option>
          ))}
        </Select>
      )}
      {tab === "open" && (
        <Select aria-label="Project" value={project} onChange={(_, d) => setProject(d.value)}>
          <option value="">All projects</option>
          {ctx.projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </Select>
      )}
    </>
  );

  return (
    <Frame
      icon={<PeopleTeam20Regular />}
      title="Workload"
      sub="Who has room, who is overbooked, and how the plan compares with the hours really logged."
      tabs={[
        ["open", "Open work"],
        ["capacity", "Capacity"],
        ["forecast", "Forecast"],
        ["utilization", "Utilization"],
        ["scenarios", "What-if plans"],
        ["setup", "Setup"],
      ]}
      tab={tab}
      setTab={setTab}
      tools={tab === "utilization" || tab === "setup" ? undefined : range}
    >
      {tab === "open" && (
        <div className={c.card}>
          <h3 className={c.cardTitle}>Open tasks by due week</h3>
          <Wait loading={open.loading && !open.data} error={open.error} empty={!open.loading && openPeople.length === 0 && "No open tasks with a due date in this range"} onRetry={open.reload} />
          {openPeople.length > 0 && (
            <div className={c.scroll}>
              <table className={c.matrix}>
                <thead>
                  <tr>
                    <th className={c.rowHead}>Person</th>
                    {weekStarts.map((w) => (
                      <th key={w}>{fmtDate(w).slice(0, 6)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {openPeople.map((p) => (
                    <tr key={p.name}>
                      <th className={c.rowHead}>{p.name}</th>
                      {weekStarts.map((w) => {
                        const it = p.cells.get(w);
                        return (
                          <td key={w}>
                            {it ? (
                              <div className={`${c.cell} ${heat(it.estimateMinutes / 60 || it.tasks, maxOpen)}`} title={`${it.tasks} tasks, ${mins(it.estimateMinutes)} estimated, ${it.overdue} overdue`}>
                                {it.tasks} {it.tasks === 1 ? "task" : "tasks"}
                                <span className={c.cellSub}>
                                  {mins(it.estimateMinutes)}
                                  {it.overdue > 0 ? ` · ${it.overdue} late` : ""}
                                </span>
                              </div>
                            ) : (
                              <div className={`${c.cell} ${c.cellEmpty}`}>—</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "capacity" && (
        <div className={c.card}>
          <h3 className={c.cardTitle}>Booked hours against capacity{scenario ? " (what-if plan)" : ""}</h3>
          <Wait loading={cap.loading && !cap.data} error={cap.error} empty={!cap.loading && (cap.data?.people ?? []).length === 0 && "No people with capacity in this workspace"} onRetry={cap.reload} />
          {(cap.data?.people ?? []).length > 0 && (
            <div className={c.scroll}>
              <table className={c.matrix}>
                <thead>
                  <tr>
                    <th className={c.rowHead}>Person</th>
                    {(cap.data?.people[0]?.weeks ?? []).map((w) => (
                      <th key={w.weekStart}>{fmtDate(w.weekStart).slice(0, 6)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(cap.data?.people ?? []).map((p) => (
                    <tr key={p.userId}>
                      <th className={c.rowHead}>{p.displayName ?? `User ${p.userId}`}</th>
                      {p.weeks.map((w) => (
                        <td key={w.weekStart}>
                          <div
                            className={`${c.cell} ${w.overAllocated ? c.heat5 : w.utilizationPercent === null ? "" : w.utilizationPercent >= 85 ? c.heat4 : w.utilizationPercent >= 50 ? c.heat2 : c.heat1}`}
                            title={`Capacity ${hours(w.capacityHours)}, booked ${hours(w.allocatedHours)}, tentative ${hours(w.tentativeHours)}, task estimates ${hours(w.plannedTaskHours)}, logged ${hours(w.loggedHours)} (${hours(w.billableHours)} billable), ${w.leaveDays} days off`}
                          >
                            {hours(w.allocatedHours)} / {hours(w.capacityHours)}
                            <span className={c.cellSub}>
                              {w.utilizationPercent === null ? "no capacity" : `${w.utilizationPercent}%`} · logged {hours(w.loggedHours)}
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={c.note}>Each cell: booked hours / capacity, then utilization and hours logged. Red means overbooked. Hover for the full breakdown.</p>
        </div>
      )}

      {tab === "forecast" && (
        <div className={c.card}>
          <h3 className={c.cardTitle}>Team capacity forecast</h3>
          {!admin ? (
            <AdminOnly />
          ) : (
            <>
              <Wait loading={fc.loading && !fc.data} error={fc.error} onRetry={fc.reload} />
              {fc.data && (
                <>
                  <LineChart
                    aria="Team capacity, booked and planned hours per week"
                    labels={fc.data.weeks.map((w) => fmtDate(w.weekStart).slice(0, 6))}
                    series={[
                      { label: "Capacity", color: "#9a9a9a", dashed: true, values: fc.data.weeks.map((w) => w.capacityHours) },
                      { label: "Booked", color: "#0f6cbd", values: fc.data.weeks.map((w) => w.bookedHours) },
                      { label: "Task estimates", color: "#f2994a", values: fc.data.weeks.map((w) => w.plannedTaskHours) },
                    ]}
                  />
                  <div className={c.scroll}>
                    <table className={c.tbl}>
                      <thead>
                        <tr>
                          <th>Week</th>
                          <th className={c.num}>Capacity</th>
                          <th className={c.num}>Booked</th>
                          <th className={c.num}>Tentative</th>
                          <th className={c.num}>Free</th>
                          <th className={c.num}>Utilization</th>
                          <th className={c.num}>Overbooked people</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fc.data.weeks.map((w) => (
                          <tr key={w.weekStart}>
                            <td>{fmtDate(w.weekStart)}</td>
                            <td className={c.num}>{hours(w.capacityHours)}</td>
                            <td className={c.num}>{hours(w.bookedHours)}</td>
                            <td className={c.num}>{hours(w.tentativeHours)}</td>
                            <td className={`${c.num} ${w.freeHours < 0 ? c.late : ""}`}>{hours(w.freeHours)}</td>
                            <td className={c.num}>{w.utilizationPercent === null ? "—" : `${w.utilizationPercent}%`}</td>
                            <td className={`${c.num} ${w.overbookedPeople > 0 ? c.late : ""}`}>{w.overbookedPeople}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {tab === "utilization" && (
        <div className={c.card}>
          <div className={c.cardHead}>
            <h3 className={c.cardTitle}>Utilization against target</h3>
            <div className={c.actions}>
              <Input type="date" aria-label="From" value={uFrom} onChange={(_, d) => d.value && setUFrom(d.value)} />
              <Input type="date" aria-label="To" value={uTo} onChange={(_, d) => d.value && setUTo(d.value)} />
            </div>
          </div>
          {!admin ? (
            <AdminOnly />
          ) : (
            <>
              <Wait loading={ut.loading && !ut.data} error={ut.error} empty={!ut.loading && (ut.data?.people ?? []).length === 0 && "No people in this workspace"} onRetry={ut.reload} />
              {(ut.data?.people ?? []).length > 0 && (
                <div className={c.scroll}>
                  <table className={c.tbl}>
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Role</th>
                        <th className={c.num}>Capacity</th>
                        <th className={c.num}>Logged</th>
                        <th className={c.num}>Billable</th>
                        <th>Billable vs target</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ut.data?.people ?? []).map((p) => (
                        <tr key={p.userId}>
                          <td className={c.strong}>{p.displayName ?? `User ${p.userId}`}</td>
                          <td>{p.role ?? "—"}</td>
                          <td className={c.num}>{hours(p.capacityHours)}</td>
                          <td className={c.num}>
                            {hours(p.loggedHours)} {p.utilizationPercent !== null && <span className={c.note}>({p.utilizationPercent}%)</span>}
                          </td>
                          <td className={c.num}>
                            {hours(p.billableHours)} {p.billablePercent !== null && <span className={c.note}>({p.billablePercent}%)</span>}
                          </td>
                          <td>
                            <Meter pct={p.billablePercent ?? 0} tone={p.billablePercent !== null && p.billablePercent >= p.targetPercent ? "good" : "warn"} />
                            <span className={c.note}>target {p.targetPercent}%</span>
                          </td>
                          <td>
                            <Tag text={p.status} tone={p.status === "ON_TARGET" ? c.chipGreen : p.status === "BELOW_TARGET" ? c.chipAmber : c.chipGrey} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "scenarios" && (
        <>
          <RecordTable<ScenarioRow>
            ctx={ctx}
            title="What-if plans"
            noun="plan"
            route="time/cap/scenario"
            query={{ workspaceId: ctx.workspaceId, pageSize: 200 }}
            base={{ workspaceId: ctx.workspaceId }}
            version={ver}
            onChanged={() => setVer((v) => v + 1)}
            intro="A plan holds tentative allocations (set a plan on an allocation in Setup). Compare it with the live plan to see what would change."
            fields={[
              { key: "name", label: "Name", type: "text", required: true },
              { key: "description", label: "Description", type: "textarea" },
              { key: "isBaseline", label: "Baseline plan", type: "bool" },
            ]}
            cols={[
              { label: "Plan", render: (r) => <span className={c.strong}>{r.name}</span> },
              { label: "About", render: (r) => r.description ?? "—" },
              { label: "", render: (r) => (r.isBaseline ? <Tag text="Baseline" tone={c.chipBlue} /> : null) },
            ]}
            rowActions={(r) => (
              <Button size="small" onClick={() => setCmp(String(r.id))}>
                Compare
              </Button>
            )}
          />
          {cmp && (
            <div className={c.card}>
              <h3 className={c.cardTitle}>Plan “{(scenarios.data ?? []).find((s) => String(s.id) === cmp)?.name ?? cmp}” against the live plan</h3>
              {!admin ? (
                <AdminOnly />
              ) : (
                <>
                  <Wait loading={cmpData.loading && !cmpData.data} error={cmpData.error} empty={!cmpData.loading && cmpData.data?.rows.length === 0 && "No differences in this range"} onRetry={cmpData.reload} />
                  {(cmpData.data?.rows ?? []).length > 0 && (
                    <div className={c.scroll}>
                      <table className={c.tbl}>
                        <thead>
                          <tr>
                            <th>Person</th>
                            <th>Week</th>
                            <th className={c.num}>Live</th>
                            <th className={c.num}>Plan</th>
                            <th className={c.num}>Change</th>
                            <th className={c.num}>Capacity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cmpData.data?.rows ?? []).map((r, i) => (
                            <tr key={i}>
                              <td>{r.displayName ?? `User ${r.userId}`}</td>
                              <td>{fmtDate(r.weekStart)}</td>
                              <td className={c.num}>{hours(r.liveHours)}</td>
                              <td className={c.num}>{hours(r.scenarioHours)}</td>
                              <td className={`${c.num} ${r.difference > 0 ? c.late : c.good}`}>
                                {r.difference > 0 ? "+" : ""}
                                {hours(r.difference)}
                              </td>
                              <td className={c.num}>{hours(r.capacityHours)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {tab === "setup" && <CapacitySetup ctx={ctx} scenarios={scenarios.data ?? []} admin={admin} onChanged={() => setVer((v) => v + 1)} />}
    </Frame>
  );
};

interface CapRowT {
  id: number;
  userId: number;
  effectiveFrom: string;
  hoursPerWeek: number;
  daysPerWeek: number;
  roleName?: string | null;
}
interface OffRowT {
  id: number;
  userId: number;
  startDate: string;
  endDate: string;
  timeOffType: string;
  status: string;
  notes?: string | null;
}
interface AllocRowT {
  id: number;
  userId: number;
  projectId?: number | null;
  startDate: string;
  endDate: string;
  hoursPerDay: number;
  kind: string;
  scenarioId?: number | null;
}
interface TargetRowT {
  id: number;
  userId?: number | null;
  roleName?: string | null;
  targetPercent: number;
}

const CapacitySetup: React.FC<{ ctx: PageCtx; scenarios: ScenarioRow[]; admin: boolean; onChanged: () => void }> = ({ ctx, scenarios, admin, onChanged }) => {
  const q: Q = { workspaceId: ctx.workspaceId, pageSize: 200 };
  const base = { workspaceId: ctx.workspaceId };
  const who = peopleOptions(ctx.people);
  const proj = ctx.projects.map((p) => ({ value: String(p.id), label: p.name }));
  const projName = (id?: number | null) => (id ? (ctx.projects.find((p) => p.id === id)?.name ?? `Project ${id}`) : "—");
  return (
    <>
      <RecordTable<CapRowT>
        ctx={ctx}
        title="Weekly capacity"
        noun="capacity"
        route="time/cap/capacity"
        query={{ ...q, sort: "-effectiveFrom" }}
        base={base}
        readOnly={!admin}
        onChanged={onChanged}
        intro="How many hours a person can work per week from a given date. The newest row that has started applies."
        fields={[
          { key: "userId", label: "Person", type: "select", options: who, numeric: true, required: true },
          { key: "effectiveFrom", label: "From", type: "date", required: true, initial: iso(new Date()) },
          { key: "hoursPerWeek", label: "Hours per week", type: "number", initial: "40" },
          { key: "daysPerWeek", label: "Days per week", type: "number", initial: "5" },
          { key: "roleName", label: "Role", type: "text" },
        ]}
        cols={[
          { label: "Person", render: (r) => <span className={c.strong}>{personName(ctx.people, r.userId)}</span> },
          { label: "From", render: (r) => fmtDate(r.effectiveFrom) },
          { label: "Hours / week", render: (r) => r.hoursPerWeek },
          { label: "Days / week", render: (r) => r.daysPerWeek },
          { label: "Role", render: (r) => r.roleName ?? "—" },
        ]}
      />
      <RecordTable<OffRowT>
        ctx={ctx}
        title="Time off"
        noun="time off"
        route="time/cap/timeoff"
        query={{ ...q, sort: "-startDate" }}
        base={base}
        onChanged={onChanged}
        fields={[
          { key: "userId", label: "Person", type: "select", options: who, numeric: true, required: true, initial: String(ctx.me.id) },
          { key: "startDate", label: "From", type: "date", required: true },
          { key: "endDate", label: "To", type: "date", required: true },
          { key: "timeOffType", label: "Type", type: "select", required: true, initial: "LEAVE", options: ["LEAVE", "SICK", "HOLIDAY", "OTHER"].map((v) => ({ value: v, label: nice(v) })) },
          { key: "status", label: "Status", type: "select", required: true, initial: "APPROVED", options: ["APPROVED", "PENDING", "REJECTED"].map((v) => ({ value: v, label: nice(v) })) },
          { key: "notes", label: "Notes", type: "text" },
        ]}
        cols={[
          { label: "Person", render: (r) => <span className={c.strong}>{personName(ctx.people, r.userId)}</span> },
          { label: "From", render: (r) => fmtDate(r.startDate) },
          { label: "To", render: (r) => fmtDate(r.endDate) },
          { label: "Type", render: (r) => nice(r.timeOffType) },
          { label: "Status", render: (r) => <Tag text={r.status} tone={r.status === "APPROVED" ? c.chipGreen : c.chipAmber} /> },
        ]}
      />
      <RecordTable<AllocRowT>
        ctx={ctx}
        title="Allocations"
        noun="allocation"
        route="time/cap/allocation"
        query={{ ...q, sort: "-startDate" }}
        base={base}
        onChanged={onChanged}
        intro="Hours per day a person is booked on a project. Hard bookings count against capacity; soft ones are tentative. Pick a what-if plan to keep it out of the live plan."
        fields={[
          { key: "userId", label: "Person", type: "select", options: who, numeric: true, required: true },
          { key: "projectId", label: "Project", type: "select", options: proj, numeric: true },
          { key: "startDate", label: "From", type: "date", required: true },
          { key: "endDate", label: "To", type: "date", required: true },
          { key: "hoursPerDay", label: "Hours per day", type: "number", required: true, initial: "4" },
          { key: "kind", label: "Kind", type: "select", required: true, initial: "HARD", options: [{ value: "HARD", label: "Hard (committed)" }, { value: "SOFT", label: "Soft (tentative)" }] },
          { key: "scenarioId", label: "What-if plan", type: "select", numeric: true, options: scenarios.map((s) => ({ value: String(s.id), label: s.name })), hint: "Leave empty for the live plan." },
        ]}
        cols={[
          { label: "Person", render: (r) => <span className={c.strong}>{personName(ctx.people, r.userId)}</span> },
          { label: "Project", render: (r) => projName(r.projectId) },
          { label: "From", render: (r) => fmtDate(r.startDate) },
          { label: "To", render: (r) => fmtDate(r.endDate) },
          { label: "Hours / day", render: (r) => r.hoursPerDay },
          { label: "Kind", render: (r) => <Tag text={r.kind} tone={r.kind === "HARD" ? c.chipBlue : c.chipAmber} /> },
          { label: "Plan", render: (r) => (r.scenarioId ? (scenarios.find((s) => s.id === r.scenarioId)?.name ?? `Plan ${r.scenarioId}`) : "Live") },
        ]}
      />
      <RecordTable<TargetRowT>
        ctx={ctx}
        title="Utilization targets"
        noun="target"
        route="time/cap/utilizationtarget"
        query={q}
        base={base}
        readOnly={!admin}
        intro="Share of capacity that should be billable. A person's own target wins over a role target; without either it is 80%."
        fields={[
          { key: "userId", label: "Person", type: "select", options: who, numeric: true },
          { key: "roleName", label: "Role (when no person)", type: "text" },
          { key: "targetPercent", label: "Target %", type: "number", required: true, initial: "80" },
        ]}
        cols={[
          { label: "Applies to", render: (r) => (r.userId ? personName(ctx.people, r.userId) : (r.roleName ?? "Everyone")) },
          { label: "Target", render: (r) => `${r.targetPercent}%` },
        ]}
      />
    </>
  );
};

/* =================================================================== *
 *  Roadmap, epics, releases, portfolios
 * =================================================================== */
interface RoadmapData {
  projects: { id: number; name: string; status: string; health?: string | null; startDate?: string | null; endDate?: string | null; color?: string | null }[];
  lanes: { lane: string; items: { id: number; projectId: number; title?: string | null; startDate?: string | null; endDate?: string | null; color?: string | null }[] }[];
  releases: { id: number; projectId: number; name: string; version?: string | null; releaseDate?: string | null; status: string }[];
  milestones: { id: number; projectId: number; name: string; dueDate: string; isReached: boolean }[];
}
interface EpicRow {
  id: number;
  title: string;
  startDate?: string | null;
  dueDate?: string | null;
  total: number;
  done: number;
  points: number;
  donePoints: number;
  percent: number;
}
interface RollupData {
  projects: { projectId: number; name: string; status: string; endDate?: string | null; totalTasks: number; doneTasks: number; overdueTasks: number; remainingMinutes: number; percentComplete: number; health: string }[];
  totals: Record<string, number | string | null>;
}
interface ReleaseRowT {
  id: number;
  projectId: number;
  name: string;
  version?: string | null;
  releaseDate?: string | null;
  status: string;
  notes?: string | null;
}
interface RoadItemT {
  id: number;
  projectId: number;
  title?: string | null;
  lane?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  color?: string | null;
}
interface PortfolioRowT {
  id: number;
  name: string;
  description?: string | null;
  status: string;
  health?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export const RoadmapPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("roadmap");
  const { pid, el } = useProjectPick(ctx);
  const [ver, setVer] = useState(0);
  const [epicOpen, setEpicOpen] = useState(false);
  const [epic, setEpic] = useState({ title: "", start: "", due: "" });
  const [pf, setPf] = useState("");
  const proj = ctx.projects.map((p) => ({ value: String(p.id), label: p.name }));
  const projName = (id: number) => ctx.projects.find((p) => p.id === id)?.name ?? `Project ${id}`;

  const road = useLoad(() => (tab === "roadmap" ? api<RoadmapData>("GET", "/api/agile/roadmap", undefined, { workspaceId: ctx.workspaceId }) : Promise.resolve(null)), [tab, ctx.workspaceId, ver]);
  const epics = useLoad(async () => (tab === "epics" && pid !== null ? asList<EpicRow>(await api("GET", `/api/agile/projects/${pid}/epics`)) : []), [tab, pid, ver]);
  const portfolios = useLoad(async () => (await crud.list<PortfolioRowT>("workspaces/ws/portfolio", { workspaceId: ctx.workspaceId })).items ?? [], [ctx.workspaceId, ver]);
  const rollup = useLoad(() => (tab === "portfolios" && pf ? api<RollupData>("GET", `/api/workspaces/portfolios/${pf}/rollup`) : Promise.resolve(null)), [tab, pf, ver]);

  const roadRows: GRow[] = useMemo(() => {
    const out: GRow[] = [];
    for (const p of road.data?.projects ?? []) if (p.startDate || p.endDate) out.push({ id: p.id, title: p.name, start: (p.startDate ?? p.endDate) as string, end: (p.endDate ?? p.startDate) as string, done: p.status === "COMPLETED", color: p.color ?? undefined });
    for (const lane of road.data?.lanes ?? [])
      for (const it of lane.items)
        if (it.startDate || it.endDate) out.push({ id: -it.id, title: `${lane.lane ? `${lane.lane}: ` : ""}${it.title ?? "Item"}`, start: (it.startDate ?? it.endDate) as string, end: (it.endDate ?? it.startDate) as string, done: false, indent: 1, color: it.color ?? undefined });
    return out;
  }, [road.data]);
  const roadMiles: GMile[] = [
    ...(road.data?.milestones ?? []).map((m) => ({ id: m.id, name: `${m.name} (${projName(m.projectId)})`, date: m.dueDate, reached: m.isReached })),
    ...(road.data?.releases ?? []).filter((r) => r.releaseDate).map((r) => ({ id: 1_000_000 + r.id, name: `Release ${r.name}${r.version ? ` ${r.version}` : ""}`, date: r.releaseDate as string, reached: r.status === "RELEASED" })),
  ];

  const addEpic = async () => {
    if (pid === null) return;
    const ok = await act(async () => {
      const st = (await crud.list<{ id: number }>(R.status, { projectId: pid, sort: "sortOrder", pageSize: 1 })).items?.[0];
      if (!st) throw new Error("This project has no statuses yet.");
      await crud.create(R.task, { workspaceId: ctx.workspaceId, projectId: pid, statusId: st.id, title: epic.title.trim(), taskType: "EPIC", startDate: epic.start || null, dueDate: epic.due || null });
    }, "Epic added.");
    if (ok) {
      setEpicOpen(false);
      setEpic({ title: "", start: "", due: "" });
      setVer((v) => v + 1);
    }
  };

  return (
    <Frame
      icon={<Map20Regular />}
      title="Roadmap"
      sub="Projects, releases and milestones on one timeline, plus epics and portfolios."
      tabs={[
        ["roadmap", "Roadmap"],
        ["epics", "Epics"],
        ["releases", "Releases"],
        ["items", "Roadmap items"],
        ["portfolios", "Portfolios"],
      ]}
      tab={tab}
      setTab={setTab}
      tools={tab === "epics" ? el : undefined}
    >
      {tab === "roadmap" && (
        <div className={c.card}>
          <h3 className={c.cardTitle}>Roadmap</h3>
          <Wait loading={road.loading && !road.data} error={road.error} onRetry={road.reload} />
          {road.data && (
            <>
              <GanttChart aria="Portfolio roadmap" rows={roadRows} milestones={roadMiles} />
              <p className={c.note}>Diamonds are milestones and releases. Add dates to projects and roadmap items to see them here.</p>
            </>
          )}
        </div>
      )}

      {tab === "epics" && (
        <div className={c.card}>
          <div className={c.cardHead}>
            <h3 className={c.cardTitle}>Epics</h3>
            <Button size="small" icon={<Add20Regular />} disabled={pid === null} onClick={() => setEpicOpen(true)}>
              New epic
            </Button>
          </div>
          <Wait loading={epics.loading && !epics.data} error={epics.error} empty={!epics.loading && (epics.data ?? []).length === 0 && "No epics in this project"} onRetry={epics.reload} />
          {(epics.data ?? []).map((e) => (
            <div key={e.id} className={c.epic}>
              <div className={c.cardHead}>
                <button type="button" className={c.itemMain} onClick={() => ctx.openTask(e.id)}>
                  <span className={c.itemTitle}>{e.title}</span>
                  <span className={c.itemMeta}>
                    {e.startDate || e.dueDate ? `${fmtDate(e.startDate)} → ${fmtDate(e.dueDate)} · ` : ""}
                    {e.done}/{e.total} tasks · {e.donePoints}/{e.points} points
                  </span>
                </button>
                <Tag text={`${e.percent}%`} tone={e.percent >= 100 ? c.chipGreen : c.chipBlue} />
              </div>
              <Meter pct={e.percent} tone={e.percent >= 100 ? "good" : undefined} />
            </div>
          ))}
          <FormDialog open={epicOpen} title="New epic" action="Add" disabled={!epic.title.trim()} onClose={() => setEpicOpen(false)} onSubmit={() => void addEpic()}>
            <Field label="Title" required>
              <Input value={epic.title} onChange={(_, d) => setEpic({ ...epic, title: d.value })} />
            </Field>
            <div className={styles.formRow}>
              <Field label="Start" className={styles.grow}>
                <Input type="date" value={epic.start} onChange={(_, d) => setEpic({ ...epic, start: d.value })} />
              </Field>
              <Field label="Due" className={styles.grow}>
                <Input type="date" value={epic.due} onChange={(_, d) => setEpic({ ...epic, due: d.value })} />
              </Field>
            </div>
          </FormDialog>
        </div>
      )}

      {tab === "releases" && (
        <RecordTable<ReleaseRowT>
          ctx={ctx}
          title="Releases"
          noun="release"
          route="agile/agl/release"
          query={{ workspaceId: ctx.workspaceId, sort: "releaseDate" }}
          base={{ workspaceId: ctx.workspaceId }}
          version={ver}
          fields={[
            { key: "projectId", label: "Project", type: "select", options: proj, numeric: true, required: true, initial: pid === null ? "" : String(pid) },
            { key: "name", label: "Name", type: "text", required: true },
            { key: "version", label: "Version", type: "text" },
            { key: "releaseDate", label: "Release date", type: "date" },
            { key: "status", label: "Status", type: "select", required: true, initial: "PLANNED", options: ["PLANNED", "IN_PROGRESS", "RELEASED", "CANCELLED"].map((v) => ({ value: v, label: nice(v) })) },
            { key: "notes", label: "Notes", type: "textarea" },
          ]}
          cols={[
            { label: "Release", render: (r) => <span className={c.strong}>{r.name}</span> },
            { label: "Version", render: (r) => r.version ?? "—" },
            { label: "Project", render: (r) => projName(r.projectId) },
            { label: "Date", render: (r) => fmtDate(r.releaseDate) },
            { label: "Status", render: (r) => <Tag text={r.status} tone={r.status === "RELEASED" ? c.chipGreen : c.chipBlue} /> },
          ]}
        />
      )}

      {tab === "items" && (
        <RecordTable<RoadItemT>
          ctx={ctx}
          title="Roadmap items"
          noun="item"
          route="agile/agl/roadmapitem"
          query={{ workspaceId: ctx.workspaceId, sort: "startDate" }}
          base={{ workspaceId: ctx.workspaceId }}
          version={ver}
          intro="Free-standing bars on the roadmap, grouped into lanes."
          fields={[
            { key: "projectId", label: "Project", type: "select", options: proj, numeric: true, required: true, initial: pid === null ? "" : String(pid) },
            { key: "title", label: "Title", type: "text", required: true },
            { key: "lane", label: "Lane", type: "text" },
            { key: "startDate", label: "Start", type: "date" },
            { key: "endDate", label: "End", type: "date" },
            { key: "color", label: "Colour (e.g. #0f6cbd)", type: "text" },
          ]}
          cols={[
            { label: "Item", render: (r) => <span className={c.strong}>{r.title ?? "—"}</span> },
            { label: "Lane", render: (r) => r.lane ?? "—" },
            { label: "Project", render: (r) => projName(r.projectId) },
            { label: "Start", render: (r) => fmtDate(r.startDate) },
            { label: "End", render: (r) => fmtDate(r.endDate) },
          ]}
        />
      )}

      {tab === "portfolios" && (
        <>
          <RecordTable<PortfolioRowT>
            ctx={ctx}
            title="Portfolios"
            noun="portfolio"
            route="workspaces/ws/portfolio"
            query={{ workspaceId: ctx.workspaceId }}
            base={{ workspaceId: ctx.workspaceId }}
            version={ver}
            onChanged={() => setVer((v) => v + 1)}
            fields={[
              { key: "name", label: "Name", type: "text", required: true },
              { key: "description", label: "Description", type: "textarea" },
              { key: "status", label: "Status", type: "select", required: true, initial: "ACTIVE", options: ["ACTIVE", "ON_HOLD", "COMPLETED", "ARCHIVED"].map((v) => ({ value: v, label: nice(v) })) },
              { key: "startDate", label: "Start", type: "date" },
              { key: "endDate", label: "End", type: "date" },
            ]}
            cols={[
              { label: "Portfolio", render: (r) => <span className={c.strong}>{r.name}</span> },
              { label: "Status", render: (r) => <Tag text={r.status} tone={r.status === "ACTIVE" ? c.chipGreen : c.chipGrey} /> },
              { label: "Start", render: (r) => fmtDate(r.startDate) },
              { label: "End", render: (r) => fmtDate(r.endDate) },
            ]}
            rowActions={(r) => (
              <Button size="small" onClick={() => setPf(String(r.id))}>
                Roll-up
              </Button>
            )}
          />
          {pf && (
            <div className={c.card}>
              <h3 className={c.cardTitle}>Roll-up: {(portfolios.data ?? []).find((p) => String(p.id) === pf)?.name ?? pf}</h3>
              <Wait loading={rollup.loading && !rollup.data} error={rollup.error} empty={!rollup.loading && rollup.data?.projects.length === 0 && "No projects in this portfolio yet. Assign projects below."} onRetry={rollup.reload} />
              {(rollup.data?.projects ?? []).length > 0 && (
                <div className={c.scroll}>
                  <table className={c.tbl}>
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Progress</th>
                        <th className={c.num}>Tasks</th>
                        <th className={c.num}>Overdue</th>
                        <th className={c.num}>Remaining</th>
                        <th>Ends</th>
                        <th>Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rollup.data?.projects ?? []).map((p) => (
                        <tr key={p.projectId}>
                          <td className={c.strong}>{p.name}</td>
                          <td>
                            <Meter pct={p.percentComplete} tone="good" />
                            <span className={c.note}>{p.percentComplete}%</span>
                          </td>
                          <td className={c.num}>
                            {p.doneTasks}/{p.totalTasks}
                          </td>
                          <td className={`${c.num} ${p.overdueTasks > 0 ? c.late : ""}`}>{p.overdueTasks}</td>
                          <td className={c.num}>{mins(p.remainingMinutes)}</td>
                          <td>{fmtDate(p.endDate)}</td>
                          <td>
                            <Tag text={p.health} tone={healthTone(p.health)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <div className={c.card}>
            <h3 className={c.cardTitle}>Projects in portfolios</h3>
            <p className={c.note}>Choose the portfolio each project belongs to.</p>
            {ctx.projects.map((p) => (
              <div key={p.id} className={c.formLine}>
                <span className={`${c.strong} ${c.grow}`}>{p.name}</span>
                <Select
                  aria-label={`Portfolio for ${p.name}`}
                  value={String((p as unknown as { portfolioId?: number | null }).portfolioId ?? "")}
                  onChange={async (_, d) => {
                    if (await act(() => crud.update(R.project, p.id, { portfolioId: d.value ? Number(d.value) : null }), "Portfolio updated.")) {
                      ctx.reloadProjects();
                      setVer((v) => v + 1);
                    }
                  }}
                >
                  <option value="">None</option>
                  {(portfolios.data ?? []).map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        </>
      )}
    </Frame>
  );
};

/* =================================================================== *
 *  Baselines & risks
 * =================================================================== */
interface VarianceData {
  baseline: { id: number; name: string; capturedAt: string };
  summary: { compared: number; late: number; early: number; onTime: number; averageSlipDays: number | null; projectEndSlipDays: number | null; added: number; removed: number; estimateDeltaMinutes: number | null };
  tasks: Record<string, unknown>[];
}
interface BaselineRowT {
  id: number;
  name: string;
  notes?: string | null;
  capturedAt: string;
}
interface HeatData {
  cells: { probability: number; impact: number; score: number; level: string; risks: { id: number; title: string }[] }[];
  total: number;
  high: number;
  medium: number;
  unmitigated: number;
  top: { id: number; title: string; score: number; level: string; status: string }[];
}
interface RiskRowT {
  id: number;
  title: string;
  description?: string | null;
  mitigation?: string | null;
  probability: number;
  impact: number;
  status: string;
  ownerUserId?: number | null;
}

export const BaselinesRisksPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const { pid, el } = useProjectPick(ctx);
  const [tab, setTab] = useState("risks");
  const [ver, setVer] = useState(0);
  const [bOpen, setBOpen] = useState(false);
  const [bName, setBName] = useState("");
  const [bNotes, setBNotes] = useState("");
  const [pick, setPick] = useState<number | null>(null);

  const bases = useLoad(async () => (tab === "baselines" && pid !== null ? asList<BaselineRowT>(await api("GET", `/api/agile/projects/${pid}/baselines`)) : []), [tab, pid, ver]);
  const vari = useLoad(() => (tab === "baselines" && pick !== null ? api<VarianceData>("GET", `/api/agile/baselines/${pick}/variance`) : Promise.resolve(null)), [tab, pick, ver]);
  const heatD = useLoad(() => (tab === "risks" && pid !== null ? api<HeatData>("GET", `/api/agile/projects/${pid}/risks/heatmap`) : Promise.resolve(null)), [tab, pid, ver]);

  const cellAt = (p: number, i: number) => heatD.data?.cells.find((x) => x.probability === p && x.impact === i);
  const levelOf = (p: number, i: number) => (p * i >= 15 ? "HIGH" : p * i >= 8 ? "MEDIUM" : "LOW");
  const riskCls: Record<string, string | undefined> = { LOW: c.riskLOW, MEDIUM: c.riskMEDIUM, HIGH: c.riskHIGH };
  const slip = (d: number | null | undefined) => (d === null || d === undefined ? "—" : `${d > 0 ? "+" : ""}${d} days`);

  const capture = async () => {
    if (pid === null) return;
    const ok = await act(() => api("POST", `/api/agile/projects/${pid}/baselines`, { name: bName.trim(), notes: bNotes.trim() || null }), "Baseline saved.");
    if (ok) {
      setBOpen(false);
      setBName("");
      setBNotes("");
      setVer((v) => v + 1);
    }
  };

  return (
    <Frame
      icon={<Warning20Regular />}
      title="Baselines & risks"
      sub="Freeze the plan to measure slip later, and keep the risk register honest."
      tabs={[
        ["risks", "Risks"],
        ["baselines", "Baselines"],
      ]}
      tab={tab}
      setTab={setTab}
      tools={el}
    >
      {pid === null && <div className={styles.emptyRow}>Create a project first</div>}

      {tab === "risks" && pid !== null && (
        <>
          <div className={c.card}>
            <h3 className={c.cardTitle}>Risk heat map</h3>
            <Wait loading={heatD.loading && !heatD.data} error={heatD.error} onRetry={heatD.reload} />
            {heatD.data && (
              <>
                <div className={c.tiles}>
                  <Tile n={heatD.data.total} label="Open risks" />
                  <Tile n={heatD.data.high} label="High" />
                  <Tile n={heatD.data.medium} label="Medium" />
                  <Tile n={heatD.data.unmitigated} label="Without a mitigation" />
                </div>
                <div className={c.riskWrap}>
                  {[5, 4, 3, 2, 1].map((p) => (
                    <React.Fragment key={p}>
                      <div className={c.riskAxis}>{p}</div>
                      {[1, 2, 3, 4, 5].map((i) => {
                        const cell = cellAt(p, i);
                        return (
                          <div key={i} className={`${c.riskCell} ${riskCls[levelOf(p, i)] ?? ""}`} title={`Probability ${p}, impact ${i}, score ${p * i}`}>
                            {(cell?.risks ?? []).slice(0, 3).map((r) => (
                              <span key={r.id} className={c.riskDot}>
                                {r.title}
                              </span>
                            ))}
                            {(cell?.risks.length ?? 0) > 3 && <span className={c.itemMeta}>+{(cell?.risks.length ?? 0) - 3} more</span>}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  <div />
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className={c.riskAxis}>
                      {i}
                    </div>
                  ))}
                </div>
                <p className={c.note}>Rows are probability (5 = almost certain), columns are impact (5 = severe). Score is probability × impact: 15 and above is high, 8 to 14 medium.</p>
              </>
            )}
          </div>
          <RecordTable<RiskRowT>
            ctx={ctx}
            title="Risk register"
            noun="risk"
            route="agile/pm/risk"
            query={{ projectId: pid, sort: "-probability" }}
            base={{ workspaceId: ctx.workspaceId, projectId: pid }}
            version={ver}
            onChanged={() => setVer((v) => v + 1)}
            fields={[
              { key: "title", label: "Risk", type: "text", required: true },
              { key: "description", label: "Description", type: "textarea" },
              { key: "probability", label: "Probability (1 to 5)", type: "number", required: true, initial: "3" },
              { key: "impact", label: "Impact (1 to 5)", type: "number", required: true, initial: "3" },
              { key: "status", label: "Status", type: "select", required: true, initial: "OPEN", options: ["OPEN", "MITIGATING", "ACCEPTED", "CLOSED"].map((v) => ({ value: v, label: nice(v) })) },
              { key: "mitigation", label: "Mitigation plan", type: "textarea" },
              { key: "ownerUserId", label: "Owner", type: "select", options: peopleOptions(ctx.people), numeric: true },
            ]}
            cols={[
              { label: "Risk", render: (r) => <span className={c.strong}>{r.title}</span> },
              { label: "P × I", render: (r) => <Tag text={`${r.probability} × ${r.impact} = ${r.probability * r.impact}`} tone={r.probability * r.impact >= 15 ? c.chipRed : r.probability * r.impact >= 8 ? c.chipAmber : c.chipGreen} /> },
              { label: "Status", render: (r) => nice(r.status) },
              { label: "Owner", render: (r) => personName(ctx.people, r.ownerUserId) },
              { label: "Mitigation", render: (r) => r.mitigation ?? "—" },
            ]}
          />
        </>
      )}

      {tab === "baselines" && pid !== null && (
        <>
          <div className={c.card}>
            <div className={c.cardHead}>
              <h3 className={c.cardTitle}>Baselines</h3>
              <Button size="small" icon={<Add20Regular />} onClick={() => setBOpen(true)}>
                Save baseline now
              </Button>
            </div>
            <p className={c.note}>A baseline is a snapshot of every task's dates, estimate and cost. Compare it later to see how far the plan has moved.</p>
            <Wait loading={bases.loading && !bases.data} error={bases.error} empty={!bases.loading && (bases.data ?? []).length === 0 && "No baselines yet"} onRetry={bases.reload} />
            {(bases.data ?? []).map((b) => (
              <div key={b.id} className={`${c.item} ${pick === b.id ? c.itemPicked : ""}`}>
                <button type="button" className={c.itemMain} onClick={() => setPick(b.id)}>
                  <span className={c.itemTitle}>{b.name}</span>
                  <span className={c.itemMeta}>
                    Saved {fmtDate(b.capturedAt)}
                    {b.notes ? ` · ${b.notes}` : ""}
                  </span>
                </button>
                <Button size="small" onClick={() => setPick(b.id)}>
                  Compare
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  aria-label="Delete baseline"
                  onClick={async () => {
                    if (await act(() => crud.remove("agile/pm/baseline", b.id), "Baseline deleted.")) {
                      if (pick === b.id) setPick(null);
                      setVer((v) => v + 1);
                    }
                  }}
                />
              </div>
            ))}
          </div>
          {pick !== null && (
            <div className={c.card}>
              <h3 className={c.cardTitle}>Plan against “{vari.data?.baseline.name ?? "baseline"}”</h3>
              <Wait loading={vari.loading && !vari.data} error={vari.error} onRetry={vari.reload} />
              {vari.data && (
                <>
                  <div className={c.tiles}>
                    <Tile n={slip(vari.data.summary.projectEndSlipDays)} label="Finish date slip" />
                    <Tile n={slip(vari.data.summary.averageSlipDays)} label="Average task slip" />
                    <Tile n={vari.data.summary.late} label="Later than baseline" />
                    <Tile n={vari.data.summary.early} label="Earlier" />
                    <Tile n={vari.data.summary.onTime} label="On time" />
                    <Tile n={vari.data.summary.added} label="Added since" />
                    <Tile n={vari.data.summary.removed} label="Removed since" />
                    <Tile n={vari.data.summary.estimateDeltaMinutes === null ? "—" : `${vari.data.summary.estimateDeltaMinutes > 0 ? "+" : ""}${mins(vari.data.summary.estimateDeltaMinutes)}`} label="Estimate change" />
                  </div>
                  <VarianceTable rows={vari.data.tasks} onPick={ctx.openTask} />
                </>
              )}
            </div>
          )}
          <FormDialog open={bOpen} title="Save baseline" action="Save" disabled={!bName.trim()} onClose={() => setBOpen(false)} onSubmit={() => void capture()}>
            <Field label="Name" required>
              <Input value={bName} onChange={(_, d) => setBName(d.value)} placeholder="e.g. Approved plan" />
            </Field>
            <Field label="Notes">
              <Textarea value={bNotes} onChange={(_, d) => setBNotes(d.value)} rows={3} />
            </Field>
          </FormDialog>
        </>
      )}
    </Frame>
  );
};

/** variance rows come back with whatever columns the server computes, so show them by name */
const VarianceTable: React.FC<{ rows: Record<string, unknown>[]; onPick: (id: number) => void }> = ({ rows, onPick }) => {
  if (rows.length === 0) return <div className={styles.emptyRow}>No tasks to compare</div>;
  const keys = Object.keys(rows[0] ?? {}).filter((k) => k !== "id" && k !== "taskId");
  const show = (k: string, v: unknown) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return fmtDate(v);
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "number" && /slip|delta/i.test(k)) return `${v > 0 ? "+" : ""}${v}`;
    return String(v);
  };
  return (
    <div className={c.scroll}>
      <table className={c.tbl}>
        <thead>
          <tr>
            {keys.map((k) => (
              <th key={k}>{nice(k.replace(/([A-Z])/g, "_$1"))}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const id = Number(r["id"] ?? r["taskId"]);
            return (
              <tr key={i} className={Number.isFinite(id) ? c.clickRow : undefined} onClick={() => Number.isFinite(id) && onPick(id)}>
                {keys.map((k) => (
                  <td key={k} className={typeof r[k] === "number" ? c.num : undefined}>
                    {show(k, r[k])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* =================================================================== *
 *  Search, saved views, calendar query, templates
 * =================================================================== */
interface SearchHit {
  type: string;
  id: number;
  workspaceId: number;
  projectId?: number | null;
  title: string;
  snippet?: string | null;
  taskId?: number | null;
}
interface TaskLite {
  id: number;
  title: string;
  priority: string;
  dueDate?: string | null;
  startDate?: string | null;
  isCompleted: boolean;
  projectId?: number | null;
  estimateMinutes?: number | null;
  storyPoints?: number | null;
  taskType?: string;
}
interface CalItem {
  type: string;
  id: number;
  title: string;
  date: string;
  endDate?: string | null;
  isCompleted?: boolean;
  projectId?: number | null;
  priority?: string | null;
}
interface SavedViewT {
  id: number;
  name: string;
  projectId?: number | null;
  viewType: string;
  entity: string;
  isShared: boolean;
  filterJson?: string | null;
  sortJson?: string | null;
  groupBy?: string | null;
}
interface TemplateT {
  id: number;
  name: string;
  description?: string | null;
  kind: string;
  category?: string | null;
  version: number;
  status: string;
  isPublic: boolean;
  contentJson: string;
}
interface TemplateVersionT {
  versionNo: number;
  changeNote?: string | null;
  createdAt: string;
}

const HIT_TYPES: [string, string][] = [
  ["task", "Tasks"],
  ["project", "Projects"],
  ["document", "Documents"],
  ["comment", "Comments"],
  ["decision", "Decisions"],
];

const TaskTable: React.FC<{ rows: TaskLite[]; ctx: PageCtx }> = ({ rows, ctx }) => (
  <div className={c.scroll}>
    <table className={c.tbl}>
      <thead>
        <tr>
          <th>Task</th>
          <th>Priority</th>
          <th>Start</th>
          <th>Due</th>
          <th className={c.num}>Estimate</th>
          <th className={c.num}>Points</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.id} className={c.clickRow} onClick={() => ctx.openTask(t.id)}>
            <td className={c.strong}>{t.title}</td>
            <td>{nice(t.priority)}</td>
            <td>{fmtDate(t.startDate)}</td>
            <td>{fmtDate(t.dueDate)}</td>
            <td className={c.num}>{t.estimateMinutes ? mins(t.estimateMinutes) : "—"}</td>
            <td className={c.num}>{t.storyPoints ?? "—"}</td>
            <td>
              <Tag text={t.isCompleted ? "Done" : "Open"} tone={t.isCompleted ? c.chipGreen : c.chipBlue} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const FindPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("search");
  const [ver, setVer] = useState(0);
  // search
  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  const [types, setTypes] = useState<string[]>(HIT_TYPES.map(([k]) => k));
  const [sProj, setSProj] = useState("");
  // saved views
  const [run, setRun] = useState<SavedViewT | null>(null);
  // calendar
  const [cFrom, setCFrom] = useState(() => iso(mondayOf(new Date())));
  const [cTo, setCTo] = useState(() => iso(new Date(Date.now() + 27 * 86400000)));
  const [cProj, setCProj] = useState("");
  const [cMine, setCMine] = useState(false);
  // templates
  const [apply, setApply] = useState<TemplateT | null>(null);
  const [aForm, setAForm] = useState({ name: "", key: "", start: iso(new Date()), portfolioId: "" });
  const [verOf, setVerOf] = useState<TemplateT | null>(null);

  const hits = useLoad(
    () => (tab === "search" && term.length >= 2 ? api<{ items: SearchHit[] }>("GET", "/api/views/search", undefined, { q: term, types: types.join(","), workspaceId: ctx.workspaceId, projectId: sProj || undefined }) : Promise.resolve(null)),
    [tab, term, types.join(","), ctx.workspaceId, sProj],
  );
  const ran = useLoad(() => (run ? api<{ view: { name: string }; items: TaskLite[] }>("GET", `/api/views/saved/${run.id}/run`, undefined, { pageSize: 200 }) : Promise.resolve(null)), [run?.id]);
  const cal = useLoad(
    () => (tab === "calendar" ? api<{ items: CalItem[] }>("GET", "/api/views/calendar", undefined, { workspaceId: ctx.workspaceId, from: cFrom, to: cTo, projectId: cProj || undefined, mine: cMine ? 1 : undefined }) : Promise.resolve(null)),
    [tab, ctx.workspaceId, cFrom, cTo, cProj, cMine],
  );
  const versions = useLoad(async () => (verOf ? asList<TemplateVersionT>(await api("GET", `/api/views/templates/${verOf.id}/versions`)) : []), [verOf?.id]);
  const portfolios = useLoad(async () => (tab === "templates" ? (await crud.list<{ id: number; name: string }>("workspaces/ws/portfolio", { workspaceId: ctx.workspaceId })).items ?? [] : []), [tab, ctx.workspaceId]);

  const toggle = (k: string, on: boolean) => setTypes((t) => (on ? [...t, k] : t.filter((x) => x !== k)));
  const open = (h: SearchHit) => {
    if (h.taskId) ctx.openTask(h.taskId);
  };
  const projName = (id?: number | null) => (id ? (ctx.projects.find((p) => p.id === id)?.name ?? `Project ${id}`) : "—");
  const grouped = useMemo(() => {
    const m = new Map<string, CalItem[]>();
    for (const it of cal.data?.items ?? []) {
      const k = it.date.slice(0, 10);
      m.set(k, [...(m.get(k) ?? []), it]);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [cal.data]);

  const doApply = async () => {
    if (!apply) return;
    let result: { projectId: number; tasksCreated: number } | null = null;
    const ok = await act(async () => {
      result = await api<{ projectId: number; tasksCreated: number }>("POST", `/api/views/templates/${apply.id}/apply`, {
        name: aForm.name.trim() || null,
        workspaceId: ctx.workspaceId,
        startDate: aForm.start || null,
        portfolioId: aForm.portfolioId ? Number(aForm.portfolioId) : null,
        projectKey: aForm.key.trim() || null,
      });
    });
    if (ok) {
      const made = result as { projectId: number; tasksCreated: number } | null;
      ctx.notify("success", `Project created with ${made?.tasksCreated ?? 0} tasks.`);
      setApply(null);
      ctx.reloadProjects();
    }
  };

  return (
    <Frame
      icon={<Search20Regular />}
      title="Find"
      sub="Search everything, run saved views, look at dates across projects, and start projects from templates."
      tabs={[
        ["search", "Search"],
        ["views", "Saved views"],
        ["calendar", "Dates"],
        ["templates", "Project templates"],
      ]}
      tab={tab}
      setTab={setTab}
    >
      {tab === "search" && (
        <div className={c.card}>
          <form
            className={c.formLine}
            onSubmit={(e) => {
              e.preventDefault();
              setTerm(text.trim());
            }}
          >
            <Field label="Search for" className={c.grow}>
              <Input value={text} onChange={(_, d) => setText(d.value)} contentBefore={<Search20Regular />} placeholder="At least 2 characters" />
            </Field>
            <Field label="Project">
              <Select value={sProj} onChange={(_, d) => setSProj(d.value)}>
                <option value="">Any project</option>
                {ctx.projects.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Button appearance="primary" type="submit" disabled={text.trim().length < 2}>
              Search
            </Button>
          </form>
          <div className={c.checks}>
            {HIT_TYPES.map(([k, label]) => (
              <Checkbox key={k} label={label} checked={types.includes(k)} onChange={(_, d) => toggle(k, d.checked === true)} />
            ))}
          </div>
          <Wait loading={hits.loading && !hits.data} error={hits.error} empty={term.length >= 2 && !hits.loading && hits.data?.items.length === 0 && `Nothing found for “${term}”`} onRetry={hits.reload} />
          {(hits.data?.items ?? []).map((h) => (
            <div key={`${h.type}-${h.id}`} className={c.item}>
              <button type="button" className={c.itemMain} onClick={() => open(h)} disabled={!h.taskId}>
                <span className={c.itemTitle}>{h.title}</span>
                {h.snippet && <span className={c.itemBody}>{h.snippet}</span>}
                <span className={c.itemMeta}>{projName(h.projectId)}</span>
              </button>
              <Tag text={h.type} tone={c.chipBlue} />
            </div>
          ))}
        </div>
      )}

      {tab === "views" && (
        <>
          <RecordTable<SavedViewT>
            ctx={ctx}
            title="Saved views"
            noun="view"
            route="views/vw/savedview"
            query={{ workspaceId: ctx.workspaceId }}
            base={{ workspaceId: ctx.workspaceId, ownerUserId: ctx.me.id, entity: "TASK" }}
            version={ver}
            intro={'A view is a saved filter and sort over tasks. Filter example: [{"field":"Priority","op":"in","value":["HIGH","URGENT"]},{"field":"DueDate","op":"lt","value":"today"}]. Sort example: [{"field":"DueDate","dir":"asc"}].'}
            fields={[
              { key: "name", label: "Name", type: "text", required: true },
              { key: "projectId", label: "Project (empty = whole workspace)", type: "select", numeric: true, options: ctx.projects.map((p) => ({ value: String(p.id), label: p.name })) },
              { key: "viewType", label: "Shown as", type: "select", required: true, initial: "LIST", options: ["LIST", "BOARD", "CALENDAR", "TIMELINE"].map((v) => ({ value: v, label: nice(v) })) },
              { key: "filterJson", label: "Filter (JSON)", type: "textarea" },
              { key: "sortJson", label: "Sort (JSON)", type: "textarea" },
              { key: "groupBy", label: "Group by", type: "text" },
              { key: "isShared", label: "Share with the workspace", type: "bool" },
            ]}
            cols={[
              { label: "View", render: (r) => <span className={c.strong}>{r.name}</span> },
              { label: "Scope", render: (r) => (r.projectId ? projName(r.projectId) : "Workspace") },
              { label: "Shown as", render: (r) => nice(r.viewType) },
              { label: "", render: (r) => (r.isShared ? <Tag text="Shared" tone={c.chipBlue} /> : null) },
            ]}
            rowActions={(r) => (
              <Button size="small" onClick={() => setRun(r)}>
                Run
              </Button>
            )}
          />
          {run && (
            <div className={c.card}>
              <h3 className={c.cardTitle}>Results: {run.name}</h3>
              <Wait loading={ran.loading && !ran.data} error={ran.error} empty={!ran.loading && ran.data?.items.length === 0 && "No tasks match this view"} onRetry={ran.reload} />
              {(ran.data?.items ?? []).length > 0 && <TaskTable rows={ran.data?.items ?? []} ctx={ctx} />}
            </div>
          )}
        </>
      )}

      {tab === "calendar" && (
        <div className={c.card}>
          <div className={c.formLine}>
            <Field label="From">
              <Input type="date" value={cFrom} onChange={(_, d) => d.value && setCFrom(d.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={cTo} onChange={(_, d) => d.value && setCTo(d.value)} />
            </Field>
            <Field label="Project">
              <Select value={cProj} onChange={(_, d) => setCProj(d.value)}>
                <option value="">All projects</option>
                {ctx.projects.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox label="Only mine" checked={cMine} onChange={(_, d) => setCMine(d.checked === true)} />
          </div>
          <p className={c.note}>A flat list of what falls due or starts in the range. The calendar view in the project toolbar shows the same dates on the shared calendar.</p>
          <Wait loading={cal.loading && !cal.data} error={cal.error} empty={!cal.loading && grouped.length === 0 && "Nothing in this range"} onRetry={cal.reload} />
          {grouped.map(([day, items]) => (
            <div key={day}>
              <div className={c.strong}>{fmtDate(day)}</div>
              {items.map((it) => (
                <div key={`${it.type}${it.id}`} className={c.item}>
                  <button type="button" className={c.itemMain} onClick={() => it.type === "task" && ctx.openTask(it.id)}>
                    <span className={c.itemTitle}>{it.title}</span>
                    <span className={c.itemMeta}>
                      {projName(it.projectId)}
                      {it.endDate && it.endDate.slice(0, 10) !== day ? ` · until ${fmtDate(it.endDate)}` : ""}
                    </span>
                  </button>
                  <Tag text={it.type} tone={it.isCompleted ? c.chipGreen : c.chipBlue} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {tab === "templates" && (
        <>
          <RecordTable<TemplateT>
            ctx={ctx}
            title="Templates"
            noun="template"
            route="views/tpl/template"
            query={{ workspaceId: ctx.workspaceId }}
            base={{ workspaceId: ctx.workspaceId }}
            version={ver}
            onChanged={() => setVer((v) => v + 1)}
            intro="Project templates hold a status set and a task tree in their content. Apply one to create a ready-made project."
            fields={[
              { key: "name", label: "Name", type: "text", required: true },
              { key: "description", label: "Description", type: "textarea" },
              { key: "kind", label: "Kind", type: "select", required: true, initial: "PROJECT", options: ["PROJECT", "BLUEPRINT", "TASK", "CHECKLIST"].map((v) => ({ value: v, label: nice(v) })) },
              { key: "category", label: "Category", type: "text" },
              { key: "status", label: "Status", type: "select", required: true, initial: "PUBLISHED", options: ["DRAFT", "PUBLISHED", "ARCHIVED"].map((v) => ({ value: v, label: nice(v) })) },
              { key: "contentJson", label: "Content (JSON)", type: "textarea", required: true, initial: "{}" },
              { key: "isPublic", label: "Visible to every workspace", type: "bool" },
            ]}
            cols={[
              { label: "Template", render: (r) => <span className={c.strong}>{r.name}</span> },
              { label: "Kind", render: (r) => nice(r.kind) },
              { label: "Category", render: (r) => r.category ?? "—" },
              { label: "Version", render: (r) => r.version },
              { label: "Status", render: (r) => <Tag text={r.status} tone={r.status === "PUBLISHED" ? c.chipGreen : c.chipGrey} /> },
            ]}
            rowActions={(r) => (
              <>
                {(r.kind === "PROJECT" || r.kind === "BLUEPRINT") && (
                  <Button
                    size="small"
                    onClick={() => {
                      setApply(r);
                      setAForm({ name: r.name, key: "", start: iso(new Date()), portfolioId: "" });
                    }}
                  >
                    Apply
                  </Button>
                )}
                <Button size="small" appearance="subtle" onClick={() => setVerOf(r)}>
                  History
                </Button>
              </>
            )}
          />
          <FormDialog open={apply !== null} title={`Create project from “${apply?.name ?? ""}”`} action="Create project" disabled={!aForm.name.trim()} onClose={() => setApply(null)} onSubmit={() => void doApply()}>
            <Field label="Project name" required>
              <Input value={aForm.name} onChange={(_, d) => setAForm({ ...aForm, name: d.value })} />
            </Field>
            <div className={styles.formRow}>
              <Field label="Project key" className={styles.grow}>
                <Input value={aForm.key} onChange={(_, d) => setAForm({ ...aForm, key: d.value.toUpperCase() })} />
              </Field>
              <Field label="Start date" className={styles.grow}>
                <Input type="date" value={aForm.start} onChange={(_, d) => setAForm({ ...aForm, start: d.value })} />
              </Field>
            </div>
            <Field label="Portfolio">
              <Select value={aForm.portfolioId} onChange={(_, d) => setAForm({ ...aForm, portfolioId: d.value })}>
                <option value="">None</option>
                {(portfolios.data ?? []).map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
          </FormDialog>
          {verOf && (
            <div className={c.card}>
              <div className={c.cardHead}>
                <h3 className={c.cardTitle}>History of “{verOf.name}”</h3>
                <Button size="small" appearance="subtle" onClick={() => setVerOf(null)}>
                  Close
                </Button>
              </div>
              <Wait loading={versions.loading && !versions.data} error={versions.error} empty={!versions.loading && (versions.data ?? []).length === 0 && "No earlier versions"} onRetry={versions.reload} />
              {(versions.data ?? []).map((v) => (
                <div key={v.versionNo} className={c.item}>
                  <div className={c.itemMain}>
                    <span className={c.itemTitle}>Version {v.versionNo}</span>
                    <span className={c.itemMeta}>
                      {fmtStamp(v.createdAt)}
                      {v.changeNote ? ` · ${v.changeNote}` : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Frame>
  );
};

/* =================================================================== *
 *  Sprint planning: backlog ranking, sprint scope, burnup
 * =================================================================== */
interface BacklogItem {
  id: number;
  title: string;
  priority: string;
  taskType: string;
  storyPoints?: number | null;
  epicId?: number | null;
  sprintId?: number | null;
  isCompleted: boolean;
}
interface BacklogData {
  count: number;
  points: number;
  unestimated: number;
  items: BacklogItem[];
}
interface SprintLite {
  id: number;
  name: string;
  status: string;
  startDate?: string | null;
  endDate?: string | null;
}
interface BurnData {
  sprint: unknown;
  days: { date: string; ideal: number | null; remaining?: number | null; completed?: number | null; scope?: number | null }[];
}

export const SprintPlanningPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const { pid, el } = useProjectPick(ctx);
  const [sid, setSid] = useState("");
  const [ver, setVer] = useState(0);
  const [incl, setIncl] = useState(false);
  const [order, setOrder] = useState<BacklogItem[] | null>(null);
  const [sel, setSel] = useState<number[]>([]);

  const sprints = useLoad(async () => (pid !== null ? (await crud.list<SprintLite>(R.sprint, { projectId: pid, sort: "-startDate" })).items ?? [] : []), [pid, ver]);
  const backlog = useLoad(
    async () => {
      if (pid === null) return null;
      const d = await api<BacklogData>("GET", `/api/agile/projects/${pid}/backlog`, undefined, { includeSprints: incl ? 1 : 0 });
      setOrder(d.items);
      setSel([]);
      return d;
    },
    [pid, incl, ver],
  );
  const burn = useLoad(() => (sid ? api<BurnData>("GET", `/api/agile/sprints/${sid}/burnup`) : Promise.resolve(null)), [sid, ver]);

  const rows = order ?? [];
  const dirty = backlog.data ? rows.map((r) => r.id).join() !== backlog.data.items.map((r) => r.id).join() : false;
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const a = next[i];
    const b = next[j];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    setOrder(next);
  };
  const sprintName = (id?: number | null) => (id ? (sprints.data?.find((s) => s.id === id)?.name ?? `Sprint ${id}`) : "Backlog");
  const sprint = sprints.data?.find((s) => String(s.id) === sid);
  const selPoints = rows.filter((r) => sel.includes(r.id)).reduce((n, r) => n + (r.storyPoints ?? 0), 0);

  const go = async (fn: () => Promise<unknown>, msg: string) => {
    if (await act(fn, msg)) setVer((v) => v + 1);
  };

  return (
    <Frame
      icon={<Rocket20Regular />}
      title="Sprint planning"
      sub="Order the backlog, pull work into a sprint and watch the scope against progress."
      tabs={[["plan", "Plan"]]}
      tab="plan"
      setTab={() => undefined}
      tools={el}
    >
      {pid === null && <div className={styles.emptyRow}>Create a project first</div>}
      {pid !== null && (
        <>
          <div className={c.card}>
            <div className={c.cardHead}>
              <h3 className={c.cardTitle}>Backlog{backlog.data ? ` · ${backlog.data.count} items · ${backlog.data.points} points` : ""}</h3>
              <div className={c.actions}>
                <Checkbox label="Show items already in sprints" checked={incl} onChange={(_, d) => setIncl(d.checked === true)} />
                <Button size="small" appearance="primary" disabled={!dirty} onClick={() => void go(() => api("POST", `/api/agile/projects/${pid}/backlog/rank`, { taskIds: rows.map((r) => r.id) }), "Order saved.")}>
                  Save order
                </Button>
              </div>
            </div>
            {backlog.data && backlog.data.unestimated > 0 && <p className={c.note}>{backlog.data.unestimated} items have no story points yet.</p>}
            <div className={c.formLine}>
              <Field label="Sprint">
                <Select value={sid} onChange={(_, d) => setSid(d.value)}>
                  <option value="">Choose a sprint</option>
                  {(sprints.data ?? []).map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name} · {nice(s.status)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button disabled={!sid || sel.length === 0} onClick={() => void go(() => api("POST", `/api/agile/sprints/${sid}/tasks`, { taskIds: sel }), `${sel.length} added to the sprint.`)}>
                Add {sel.length > 0 ? `${sel.length} (${selPoints} pts) ` : ""}to sprint
              </Button>
              <Button disabled={!sid || sel.length === 0} onClick={() => void go(() => api("POST", `/api/agile/sprints/${sid}/remove-tasks`, { taskIds: sel }), `${sel.length} sent back to the backlog.`)}>
                Remove from sprint
              </Button>
            </div>
            <Wait loading={backlog.loading && !backlog.data} error={backlog.error} empty={!backlog.loading && rows.length === 0 && "The backlog is empty"} onRetry={backlog.reload} />
            {rows.map((r, i) => (
              <div key={r.id} className={`${c.item} ${sel.includes(r.id) ? c.itemPicked : ""}`}>
                <Checkbox checked={sel.includes(r.id)} aria-label={`Select ${r.title}`} onChange={(_, d) => setSel((s) => (d.checked === true ? [...s, r.id] : s.filter((x) => x !== r.id)))} />
                <button type="button" className={c.itemMain} onClick={() => ctx.openTask(r.id)}>
                  <span className={c.itemTitle}>{r.title}</span>
                  <span className={c.itemMeta}>
                    {nice(r.taskType)} · {nice(r.priority)} · {r.storyPoints ?? "no"} pts · {sprintName(r.sprintId)}
                  </span>
                </button>
                <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />} aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} />
                <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />} aria-label="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)} />
              </div>
            ))}
          </div>

          {sid && (
            <div className={c.card}>
              <h3 className={c.cardTitle}>
                Burn-up{sprint ? ` · ${sprint.name}` : ""}
                {sprint?.startDate ? ` (${fmtDate(sprint.startDate)} to ${fmtDate(sprint.endDate)})` : ""}
              </h3>
              <Wait loading={burn.loading && !burn.data} error={burn.error} empty={!burn.loading && burn.data?.days.length === 0 && "Give the sprint start and end dates to see its chart"} onRetry={burn.reload} />
              {burn.data && burn.data.days.length > 0 && (
                <LineChart
                  aria="Sprint burn-up"
                  labels={burn.data.days.map((d) => fmtDate(d.date).slice(0, 6))}
                  series={[
                    { label: "Scope", color: "#9a9a9a", dashed: true, values: burn.data.days.map((d) => d.scope ?? null) },
                    { label: "Ideal", color: "#c9c9c9", dashed: true, values: burn.data.days.map((d) => d.ideal ?? null) },
                    { label: "Completed", color: "#2ea043", values: burn.data.days.map((d) => d.completed ?? null) },
                  ]}
                />
              )}
            </div>
          )}
        </>
      )}
    </Frame>
  );
};